import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api, fullName, formatDate, mensajeError } from '../lib/api';
import { telefonoConPrefijo } from '../lib/utils';
import { useAuth } from '../lib/auth';
import { isAdmin, isManagement } from '../lib/roles';
import { estadoFunnel } from '../lib/funnel';
import { PageHeader, EmptyState } from '../components/Common';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Plus, Search, ChevronRight, Users, Trash2, FileUp, Loader2, Sparkles, ShieldAlert, Combine, FileSpreadsheet, Download, CalendarPlus, AlertTriangle, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import DarCitaDialog from '../components/citas/DarCitaDialog';
import PatientFormDialog from '../components/PatientFormDialog';

// DÓNDE CAMBIA EL NOMBRE DEL PACIENTE Y DÓNDE NO (supervisión, 2026-09-21)
// ---------------------------------------------------------------------------
// Un montón de apuntes guardan una COPIA del nombre además del `patient_id`, y
// es esa copia la que se pinta en pantalla y por la que se busca. La fusión
// refresca la de los apuntes OPERATIVOS —si no, la misma persona sale con dos
// nombres distintos en la Caja del día, en Cobros y en la lista de espera, que
// es justo la confusión que la fusión venía a quitar— pero NO la de los
// documentos ya FIRMADOS: ahí el nombre es el que se imprimió y el que el
// paciente firmó, y lo que se guarda tiene que poder reproducirse tal cual.
//
// Eso hay que decirlo ANTES, no descubrirlo tres semanas después buscando una
// receta por el nombre bueno y no encontrándola. Va en los dos diálogos —el de
// fusionar a mano y el de fusionar todos— porque la consecuencia es la misma.
function AvisoNombreEnLosApuntes() {
  return (
    <div className="p-3 rounded-md border border-border text-xs text-muted-foreground" data-testid="patients-fusion-nombres">
      <strong className="text-[#13231E]">El nombre del paciente en los apuntes:</strong>{' '}
      en las citas, los cobros, las devoluciones, los presupuestos, la lista de espera, las
      solicitudes de factura, el registro de quirófano y la bandeja de WhatsApp pasa a ser el de la
      ficha que se queda. En los documentos ya firmados (consentimientos, LOPD, hojas y registros de
      anestesia, informes, recetas, justificantes) se queda el nombre de entonces: es el que el
      paciente firmó y tiene que poder reproducirse tal cual. Buscar esos por el nombre viejo
      seguirá funcionando; por el nuevo, no siempre.
    </div>
  );
}

export default function PatientsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [dedupeBusy, setDedupeBusy] = useState(false);
  const importInputRef = React.useRef(null);
  // Phase 27 — "Dar cita" directamente desde la fila del paciente.
  const [darCitaPatient, setDarCitaPatient] = useState(null);

  // -------------------------------------------------------------------------
  // FUSIÓN DE PACIENTES (2026-09-17)
  // -------------------------------------------------------------------------
  // «podamos seleccionar las dos fichas (o más) y darle a fusionar». Se marcan
  // a mano, se elige cuál MANDA (la principal) y antes de tocar nada el
  // servidor devuelve un simulacro: qué campos quedan, qué se descarta y
  // cuántos registros se mueven. No se fusiona hasta que se dice que sí
  // expresamente, porque esto NO SE PUEDE DESHACER.
  //
  // La puerta la abre `isManagement` y no `isAdmin`: el servidor deja fusionar
  // a admin y a dirección, y una pantalla que esconde lo que el servidor
  // permite hace que recepción tenga que pedírselo a dirección para nada.
  const puedeFusionar = isManagement(user);
  const [seleccion, setSeleccion] = useState([]);
  const [principalId, setPrincipalId] = useState(null);
  const [fusion, setFusion] = useState(null);       // informe del simulacro
  const [fusionBusy, setFusionBusy] = useState(false);
  const [fusionOk, setFusionOk] = useState(false);  // la casilla de «lo he comprobado»
  const [todos, setTodos] = useState(null);         // simulacro de «Fusionar todos»
  const [todosOk, setTodosOk] = useState(false);

  // LO MARCADO SOBREVIVE AL BUSCADOR (supervisión, 2026-09-17). `marcados`
  // salía de filtrar `items` —lo que devolvió la última búsqueda— mientras el
  // contador y el botón miraban `seleccion`: al escribir en el buscador, una
  // ficha marcada que ya no salía en la lista desaparecía de `marcados` y
  // seguía contando. La barra decía «2 fichas marcadas» y se fusionaba una
  // sola. Ahora la ficha se guarda entera al marcarla, y si sigue en la lista
  // se usa la copia recién traída del servidor.
  const [fichasMarcadas, setFichasMarcadas] = useState({});
  const marcados = seleccion
    .map(id => items.find(p => p.id === id) || fichasMarcadas[id])
    .filter(Boolean);
  // Manda la que tiene NIF (la que firmó la LOPD) salvo que se diga otra cosa.
  const principal = marcados.find(p => p.id === principalId)
    || marcados.find(p => (p.nif || '').trim())
    || marcados[0]
    || null;

  const alternarSeleccion = (p) => {
    setFichasMarcadas(prev => ({ ...prev, [p.id]: p }));
    setSeleccion(prev => (prev.includes(p.id) ? prev.filter(x => x !== p.id) : [...prev, p.id]));
  };
  const limpiarSeleccion = () => {
    setSeleccion([]); setFichasMarcadas({}); setPrincipalId(null);
  };

  const errorDe = (e) => e?.response?.data?.detail || 'Error desconocido';

  // Los consentimientos y las casillas de riesgo salen aparte y no mezclados
  // con el resto de campos: son lo único de la fusión donde NO manda la ficha
  // principal. Y van al revés la una de la otra a propósito (supervisión,
  // 2026-09-18): en un consentimiento lo seguro es el «no», en una alergia lo
  // seguro es el «sí». Cada una con su bloque y su explicación, para que nadie
  // lea una y suponga la otra.
  //
  // Y EL DOCUMENTO FIRMADO, TAMBIÉN APARTE (supervisión, 2026-09-21): el
  // cuestionario de salud salía con grupo 'ficha' y acción 'descarta', o sea
  // debajo del rótulo «Datos de ficha (no clínicos) que NO se aplican». Es
  // falso por los dos lados —es clínico y está firmado— y contradice al bloque
  // ámbar de tres líneas más arriba, que está enseñando lo contrario.
  const esConsentimiento = (c) => c.grupo === 'consentimiento';
  const esRiesgo = (c) => c.grupo === 'riesgo';
  const esDocumento = (c) => c.grupo === 'documento';
  const esAparte = (c) => esConsentimiento(c) || esRiesgo(c) || esDocumento(c);

  /** Paso 1: el simulacro. No escribe nada; solo trae el informe. */
  const verFusion = async () => {
    if (!principal || marcados.length < 2) return;
    setFusionBusy(true);
    setFusionOk(false);
    try {
      const res = await api.post('/api/patients/fusionar', {
        principal_id: principal.id,
        descartadas_ids: marcados.filter(p => p.id !== principal.id).map(p => p.id),
        aplicar: false,
      });
      setFusion(res.data);
    } catch (e) {
      toast.error(errorDe(e));
    } finally {
      setFusionBusy(false);
    }
  };

  /** Paso 2: fusionar de verdad, ya con la confirmación marcada. */
  const aplicarFusion = async () => {
    if (!fusion || !fusionOk) return;
    setFusionBusy(true);
    try {
      const res = await api.post('/api/patients/fusionar', {
        principal_id: fusion.principal.id,
        descartadas_ids: fusion.descartadas.map(d => d.id),
        aplicar: true,
      });
      toast.success(
        `Fusionadas ${res.data?.descartadas?.length || 0} fichas en ${res.data?.resultado?.nombre || ''} · ` +
        `${res.data?.total_referencias_movidas || 0} registros movidos.`
      );
      setFusion(null);
      limpiarSeleccion();
      fetchItems(q);
    } catch (e) {
      toast.error(errorDe(e));
    } finally {
      setFusionBusy(false);
    }
  };

  // Phase 23.19 — «Fusionar todos»: los duplicados que el sistema detecta solo
  // (uno con NIF que firmó la LOPD + otro sin NIF auto-creado desde la
  // agenda). La detección sigue siendo esa y no «mismo nombre y teléfono»: dos
  // hermanas con el mismo móvil no son la misma persona, y esto no se deshace.
  const onRunDedupe = async () => {
    setDedupeBusy(true);
    setTodosOk(false);
    try {
      const r1 = await api.post('/api/patients/dedupe-lopd?apply=false');
      if ((r1.data?.duplicates_found || 0) === 0) {
        toast.info('No hay duplicados que limpiar. Todos los pacientes están bien.');
        return;
      }
      setTodos(r1.data);   // la lista COMPLETA, antes de tocar nada
    } catch (e) {
      toast.error(errorDe(e));
    } finally {
      setDedupeBusy(false);
    }
  };

  const aplicarDedupe = async () => {
    if (!todosOk) return;
    setDedupeBusy(true);
    try {
      const r2 = await api.post('/api/patients/dedupe-lopd?apply=true');
      toast.success(
        `Fusionados ${r2.data?.merged || 0} duplicados · ` +
        `${r2.data?.references_moved || 0} registros movidos.`
      );
      setTodos(null);
      limpiarSeleccion();
      fetchItems(q);
    } catch (e) {
      toast.error(errorDe(e));
    } finally {
      setDedupeBusy(false);
    }
  };

  const fetchItems = useCallback(async (query) => {
    setLoading(true);
    try {
      const params = query ? { q: query } : {};
      const res = await api.get('/api/patients', { params });
      setItems(res.data);
    } finally { setLoading(false); }
  }, []);

  // Phase 25.H — Bulk import de pacientes antiguos.
  const bulkInputRef = React.useRef(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const onBulkImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBulkBusy(true);
    setBulkResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/api/patients/bulk-import', fd);
      setBulkResult(res.data);
      const c = res.data?.created_count || 0;
      const s = res.data?.skipped_count || 0;
      const er = res.data?.error_count || 0;
      toast.success(`Importación: ${c} creados, ${s} duplicados, ${er} errores`);
      fetchItems(q);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error en la importación');
    } finally {
      setBulkBusy(false);
      if (bulkInputRef.current) bulkInputRef.current.value = '';
    }
  };
  const downloadBulkTemplate = async () => {
    try {
      const res = await api.get('/api/patients/bulk-import/template.xlsx', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'plantilla_pacientes.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('No se pudo descargar la plantilla');
    }
  };

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => {
    const t = setTimeout(() => fetchItems(q), 250);
    return () => clearTimeout(t);
  }, [q, fetchItems]);

  // Una ficha con documentos firmados NO se borra (23/9/2026): el servidor
  // contesta 409 con el motivo y la salida («si es un duplicado, fusiónalo con
  // la ficha buena»). La lista no sabe qué papeles tiene cada paciente, así que
  // el botón se queda; lo que no puede pasar es que el 409 se pierda en
  // silencio: se enseña el motivo tal cual lo da el servidor.
  const onDelete = async (id) => {
    if (!window.confirm('¿Eliminar paciente?')) return;
    try {
      await api.delete(`/api/patients/${id}`);
    } catch (err) {
      toast.error(mensajeError(err, 'No se pudo eliminar el paciente'), { duration: 10000 });
      return;
    }
    toast.success('Paciente eliminado');
    fetchItems(q);
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportBusy(true);
    setExtracted(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/api/patients/extract-from-document', fd);
      setExtracted(res.data);
      setEditing({
        name: res.data.name || '', surname1: res.data.surname1 || '', surname2: res.data.surname2 || '',
        nif: res.data.nif || '', birth_date: res.data.birth_date || '', email: res.data.email || '', phone: res.data.phone || '',
        address: res.data.address || { calle: '', poblacion: '', provincia: '', codigo_postal: '' },
        profession: res.data.profession || '', how_known_us: res.data.how_known_us || '',
        notes: res.data.notes || '',
      });
      toast.success(`Datos extraídos (confianza: ${res.data.confidence || 'media'})`);
      setImportOpen(false);
      setDialogOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error extrayendo datos');
    } finally {
      setImportBusy(false);
      if (importInputRef.current) importInputRef.current.value = '';
    }
  };

  return (
    <div>
      <PageHeader
        title="Pacientes"
        subtitle={`${items.length} pacientes registrados`}
        actions={
          <>
            <input ref={importInputRef} type="file" accept="application/pdf,image/png,image/jpeg,image/webp" className="hidden" onChange={onImportFile} data-testid="patient-import-input" />
            {/* Phase 25.H — input oculto para bulk import Excel/CSV */}
            <input ref={bulkInputRef} type="file" accept=".xlsx,.xlsm,.csv" className="hidden" onChange={onBulkImport} data-testid="patient-bulk-input" />
            {puedeFusionar && (
              <Button
                onClick={onRunDedupe}
                variant="outline"
                disabled={dedupeBusy}
                title="Fusionar de golpe todos los duplicados que el sistema detecta (el que firmó la LOPD, con NIF, + la ficha sin NIF que creó la agenda)"
                data-testid="patient-dedupe-button"
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
              >
                {dedupeBusy
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Buscando…</>
                  : <><Combine className="w-4 h-4 mr-2" /> Fusionar todos los duplicados</>}
              </Button>
            )}
            {/* Phase 25.H — Bulk import de pacientes antiguos pendientes de LOPD */}
            {isAdmin(user) && (
              <Button
                onClick={() => bulkInputRef.current?.click()}
                variant="outline"
                disabled={bulkBusy}
                title="Importar pacientes antiguos desde Excel/CSV (pendientes de LOPD)"
                data-testid="patient-bulk-import-button"
                className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
              >
                {bulkBusy
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importando…</>
                  : <><FileSpreadsheet className="w-4 h-4 mr-2" /> Importar pacientes (Excel)</>}
              </Button>
            )}
            <Button onClick={() => importInputRef.current?.click()} variant="outline" disabled={importBusy} data-testid="patient-import-button">
              {importBusy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Extrayendo…</> : <><Sparkles className="w-4 h-4 mr-2" /> Importar LOPD (PDF/foto)</>}
            </Button>
            <Button onClick={() => { setEditing(null); setDialogOpen(true); }} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="patient-new-button">
              <Plus className="w-4 h-4 mr-2" /> Nuevo paciente
            </Button>
          </>
        }
      />
      {extracted && (
        <div className="cp-card p-3 mb-4 bg-[rgba(46,110,94,0.06)] flex items-start gap-2">
          <Sparkles className="w-4 h-4 text-[#2E6E5E] mt-0.5" />
          <div className="text-sm">
            <strong>Datos extraídos del documento.</strong> Revisa y confirma en el formulario (confianza: {extracted.confidence || 'media'}).
            {extracted.notes && <div className="text-xs text-muted-foreground mt-0.5">Notas: {extracted.notes}</div>}
          </div>
        </div>
      )}

      {/* Phase 25.H — Resultado de la importación masiva */}
      {bulkResult && (
        <div className="cp-card p-3 mb-4 bg-emerald-50 border border-emerald-200" data-testid="bulk-import-result">
          <div className="flex items-start justify-between gap-3">
            <div className="text-sm flex-1 min-w-0">
              <div className="font-semibold text-emerald-900 mb-1">
                Importación completada: {bulkResult.total_rows} filas procesadas
              </div>
              <div className="flex flex-wrap gap-3 text-xs">
                <span className="text-emerald-800"><strong>{bulkResult.created_count}</strong> pacientes creados</span>
                <span className="text-amber-800"><strong>{bulkResult.skipped_count}</strong> duplicados (no se crean)</span>
                {bulkResult.error_count > 0 && <span className="text-red-800"><strong>{bulkResult.error_count}</strong> errores</span>}
              </div>
              {bulkResult.skipped_count > 0 && (
                <details className="mt-2 text-xs">
                  <summary className="cursor-pointer text-amber-800">Ver duplicados</summary>
                  <ul className="ml-4 mt-1 list-disc">
                    {bulkResult.skipped.slice(0, 10).map((s, i) => (
                      <li key={i}>Fila {s.row}: {s.name} → {s.reason}</li>
                    ))}
                    {bulkResult.skipped.length > 10 && <li>… y {bulkResult.skipped.length - 10} más</li>}
                  </ul>
                </details>
              )}
              {bulkResult.error_count > 0 && (
                <details className="mt-2 text-xs">
                  <summary className="cursor-pointer text-red-800">Ver errores</summary>
                  <ul className="ml-4 mt-1 list-disc">
                    {bulkResult.errors.slice(0, 10).map((s, i) => (
                      <li key={i}>Fila {s.row}: {s.error}</li>
                    ))}
                  </ul>
                </details>
              )}
              <button
                onClick={downloadBulkTemplate}
                className="text-xs text-emerald-700 hover:underline mt-2 flex items-center gap-1"
                data-testid="bulk-import-template-link"
              >
                <Download className="w-3 h-3" /> Descargar plantilla Excel
              </button>
            </div>
            <button
              onClick={() => setBulkResult(null)}
              className="text-emerald-700 hover:text-emerald-900 text-lg leading-none"
              aria-label="Cerrar"
            >×</button>
          </div>
        </div>
      )}

      <div className="cp-card p-4 mb-4">
        <div className="relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, NIF, email…" className="pl-9" data-testid="patients-search-input" />
        </div>
      </div>

      {/* La barra de la fusión manual: aparece al marcar fichas y solo deja
          seguir cuando hay DOS o más. Aquí se elige cuál manda. */}
      {puedeFusionar && marcados.length > 0 && (
        <div className="cp-card p-4 mb-4 border border-amber-300 bg-amber-50" data-testid="patients-fusion-bar">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-amber-900">
              <strong>{marcados.length} ficha{marcados.length === 1 ? '' : 's'} marcada{marcados.length === 1 ? '' : 's'}</strong>
              {marcados.length < 2
                ? ' · marca al menos dos para poder fusionarlas'
                : ' · elige cuál es la ficha PRINCIPAL (es la que manda en los datos)'}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={limpiarSeleccion} data-testid="patients-fusion-clear">
                Quitar selección
              </Button>
              <Button
                onClick={verFusion}
                disabled={marcados.length < 2 || fusionBusy}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="patients-fusion-review"
              >
                {fusionBusy
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Comprobando…</>
                  : <><Combine className="w-4 h-4 mr-2" /> Ver qué se va a fusionar</>}
              </Button>
            </div>
          </div>
          {marcados.length >= 2 && (
            <div className="mt-3 flex flex-wrap gap-4" data-testid="patients-fusion-principal">
              {marcados.map(p => (
                <label key={p.id} className="flex items-center gap-2 text-sm text-amber-900 cursor-pointer">
                  <input
                    type="radio"
                    name="fusion-principal"
                    checked={!!principal && principal.id === p.id}
                    onChange={() => setPrincipalId(p.id)}
                    data-testid={`patients-fusion-principal-${p.id}`}
                  />
                  <span>
                    {fullName(p) || '(sin nombre)'}
                    <span className="text-amber-700"> · nº {p.num_historia || '—'}{p.nif ? ` · ${p.nif}` : ' · sin NIF'}</span>
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="cp-card overflow-hidden">
        {loading ? (
          <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>
        ) : items.length === 0 ? (
          <EmptyState icon={Users} title="Sin pacientes" description={q ? 'Sin coincidencias.' : 'Crea el primer paciente.'} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[rgba(46,110,94,0.04)] text-left">
                <tr>
                  {puedeFusionar && <th className="py-3 pl-4 w-8"></th>}
                  <th className="py-3 px-4 font-semibold text-[#13231E]">Nº Hist.</th>
                  <th className="py-3 px-4 font-semibold text-[#13231E]">Paciente</th>
                  <th className="py-3 px-4 font-semibold text-[#13231E]">NIF</th>
                  <th className="py-3 px-4 font-semibold text-[#13231E]">Teléfono</th>
                  <th className="py-3 px-4 font-semibold text-[#13231E]">Email</th>
                  <th className="py-3 px-4 font-semibold text-[#13231E]">Alta</th>
                  <th className="py-3 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {items.map(p => (
                  <tr key={p.id} className={`border-t border-border hover:bg-[rgba(46,110,94,0.03)] ${seleccion.includes(p.id) ? 'bg-amber-50' : (!p.has_lopd ? 'bg-[rgba(220,38,38,0.04)]' : '')}`} data-testid="patient-row">
                    {puedeFusionar && (
                      <td className="py-3 pl-4">
                        <input
                          type="checkbox"
                          checked={seleccion.includes(p.id)}
                          onChange={() => alternarSeleccion(p)}
                          aria-label={`Marcar ${fullName(p) || 'paciente'} para fusionar`}
                          title="Marcar para fusionar con otra ficha"
                          data-testid="patient-fusion-checkbox"
                        />
                      </td>
                    )}
                    <td className="py-3 px-4 tabular-nums text-muted-foreground">{p.num_historia || '—'}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Link to={`/pacientes/${p.id}`} target="_blank" rel="noopener" className="font-medium text-[#13231E] hover:underline">{fullName(p) || '—'}</Link>
                        {!p.has_lopd && (
                          <Link to={`/lopd?patient=${p.id}`} title="Generar LOPD ahora" className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-300 hover:bg-red-100" data-testid="patient-lopd-pending-badge">
                            <ShieldAlert className="w-3 h-3" />
                            Pendiente LOPD
                          </Link>
                        )}
                        {(() => {
                          const f = estadoFunnel(p);
                          return f ? (
                            <span className={`inline-flex items-center text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border ${f.cls}`} data-testid="patient-funnel-chip">
                              {f.label}
                            </span>
                          ) : null;
                        })()}
                      </div>
                    </td>
                    <td className="py-3 px-4">{p.nif || '—'}</td>
                    <td className="py-3 px-4 whitespace-nowrap">{telefonoConPrefijo(p.phone) || '—'}</td>
                    <td className="py-3 px-4 truncate max-w-[200px]">{p.email || '—'}</td>
                    <td className="py-3 px-4 text-xs text-muted-foreground">{formatDate(p.created_at)}</td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <Button size="sm" variant="ghost" onClick={() => setDarCitaPatient(p)} title="Dar cita" className="text-[#13231E]" data-testid="patient-dar-cita"><CalendarPlus className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => { setEditing(p); setDialogOpen(true); }} data-testid="patient-edit-button">Editar</Button>
                        <Button size="sm" variant="ghost" onClick={() => onDelete(p.id)} className="text-destructive" data-testid="patient-delete-button"><Trash2 className="w-3.5 h-3.5" /></Button>
                        <Link to={`/pacientes/${p.id}`} target="_blank" rel="noopener"><Button size="sm" variant="ghost"><ChevronRight className="w-4 h-4" /></Button></Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ------------------------------------------------------------------
          CONFIRMAR LA FUSIÓN MANUAL. Lo que se enseña aquí es lo único que
          va a poder leer nadie antes de que la cosa sea irreversible: qué
          ficha se queda, qué campos van a quedar, qué valores se descartan
          (que NO se borran: quedan guardados dentro de la ficha) y cuántos
          registros se mueven de sitio.
          ------------------------------------------------------------------ */}
      <Dialog open={!!fusion} onOpenChange={(v) => { if (!v) { setFusion(null); setFusionOk(false); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="patients-fusion-dialog">
          <DialogHeader>
            <DialogTitle>Fusionar fichas de paciente</DialogTitle>
          </DialogHeader>
          {fusion && (
            <div className="space-y-4 text-sm">
              <div className="flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-300 text-red-800">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <div data-testid="patients-fusion-aviso">
                  <strong>Esto no se puede deshacer.</strong> Comprueba que son la misma persona:
                  se juntan sus historias clínicas, sus fotos, sus consentimientos y su dinero.
                </div>
              </div>

              <div className="p-3 rounded-md border border-emerald-300 bg-emerald-50" data-testid="patients-fusion-resultado">
                <div className="font-semibold text-emerald-900">Se queda esta ficha</div>
                <div className="mt-1">
                  {fusion.resultado?.nombre} · nº de historia <strong>{fusion.principal?.num_historia ?? '—'}</strong>
                  {fusion.resultado?.nif ? ` · NIF ${fusion.resultado.nif}` : ''}
                  {fusion.resultado?.phone ? ` · ${telefonoConPrefijo(fusion.resultado.phone)}` : ''}
                  {fusion.resultado?.email ? ` · ${fusion.resultado.email}` : ''}
                </div>
                {(fusion.num_historia_descartados || []).length > 0 && (
                  <div className="mt-1 text-xs text-emerald-800">
                    El número de historia de la otra ficha ({(fusion.num_historia_descartados || []).join(', ')})
                    no se reutiliza, pero se guarda: buscando por él se seguirá encontrando al paciente.
                  </div>
                )}
              </div>

              {/* LA FUSIÓN PUEDE DEJARLA EN DOS SITIOS A LA VEZ (23/9/2026): el
                  servidor lista las citas que, juntas en una ficha, chocarían
                  (una de quirófano). No se bloquea: se dice, con la salida. */}
              {(fusion.citas_solapadas || []).length > 0 && (
                <div className="flex items-start gap-2 p-3 rounded-md border border-red-300 bg-red-50 text-red-800" role="alert" data-testid="patients-fusion-citas-solapadas">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <ul className="space-y-1">
                    {fusion.citas_solapadas.map((s) => <li key={(s.citas || []).map((c) => c.cita_id).join('|')}>{s.texto}</li>)}
                  </ul>
                </div>
              )}

              {(fusion.descartadas || []).map(d => (
                <div key={d.id} className="p-3 rounded-md border border-border" data-testid="patients-fusion-descartada">
                  <div className="font-semibold text-[#13231E] flex items-center gap-2">
                    <span>{d.nombre} · nº {d.num_historia ?? '—'}</span>
                    <ArrowRight className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="text-muted-foreground">se funde y desaparece de la lista</span>
                  </div>

                  {/* LOS CONSENTIMIENTOS, EN SU PROPIO BLOQUE Y LOS PRIMEROS
                      (supervisión, 2026-09-17). No siguen la regla de «manda la
                      principal»: gana el más restrictivo, porque si el paciente
                      dijo que NO en una de las dos fichas, dijo que no. */}
                  {d.campos.filter(esConsentimiento).length > 0 && (
                    <div className="mt-2 p-2 rounded-md border border-red-300 bg-red-50" data-testid="patients-fusion-consentimientos">
                      <div className="text-xs font-semibold uppercase tracking-wide text-red-800">
                        Consentimientos · manda el más restrictivo
                      </div>
                      <ul className="mt-1 ml-4 list-disc text-xs text-red-900">
                        {d.campos.filter(esConsentimiento).map(c => (
                          <li key={c.campo}>
                            <strong>{c.etiqueta}:</strong> queda «{c.resultado}»
                            {c.accion === 'restringe' ? ' — una de las dos fichas decía que no' : ''}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* LAS CASILLAS DE RIESGO, APARTE Y CON LA REGLA
                      CONTRARIA (supervisión,
                      2026-09-18). Aquí gana el «SÍ»: si una de las dos fichas
                      apunta la alergia o el antecedente quirúrgico, la ficha
                      que queda lo apunta. Antes mandaba la principal, y una
                      fusión podía dejar a una alérgica al yodo con la casilla
                      en «No» — que es lo que lee la hoja de anestesia. */}
                  {d.campos.filter(esRiesgo).length > 0 && (
                    <div className="mt-2 p-2 rounded-md border border-amber-400 bg-amber-50" data-testid="patients-fusion-riesgo">
                      <div className="text-xs font-semibold uppercase tracking-wide text-amber-900">
                        Alergias y antecedentes · manda el «sí»
                      </div>
                      <ul className="mt-1 ml-4 list-disc text-xs text-amber-900">
                        {d.campos.filter(esRiesgo).map(c => (
                          <li key={c.campo}>
                            <strong>{c.etiqueta}:</strong> queda «{c.resultado}»
                            {c.accion === 'marca'
                              ? ` — la ficha principal decía «${c.principal}» y la otra «sí»: en duda, el riesgo se apunta`
                              : ''}
                            {c.accion === 'descarta'
                              ? ` — la otra ficha decía «${c.descartada}», pero un riesgo apuntado no se apaga al fusionar`
                              : ''}
                            {/* LA NEGACIÓN QUE SE SUSTITUYE (2026-09-21). Es el
                                único sitio de la fusión donde algo escrito deja
                                de verse en la ficha, así que se dice entero:
                                qué se quita, qué queda y por qué. Lo que se
                                quita es una frase que dice que no hay nada, no
                                un dato del paciente — y queda guardado en el
                                archivo de la fusión. */}
                            {c.accion === 'sustituye'
                              ? ` — la ficha principal tenía escrito «${c.principal}», que no apunta ningún riesgo: lo sustituye el riesgo de la otra ficha en vez de quedarse encima. La frase que se quita no se borra: queda en el archivo de la fusión`
                              : ''}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* UN DOCUMENTO FIRMADO NO SE MEZCLA CAMPO A CAMPO
                      (supervisión, 2026-09-21). El cuestionario de salud es el
                      archivo de lo que contestó el paciente desde su móvil, con
                      su fecha y su firma dentro: juntar las casillas de una
                      ficha con los textos de la otra fabricaba un cuestionario
                      que no contestó nadie. Va entero o no va. */}
                  {d.campos.filter(esDocumento).length > 0 && (
                    <div className="mt-2 p-2 rounded-md border border-sky-300 bg-sky-50" data-testid="patients-fusion-documentos">
                      <div className="text-xs font-semibold uppercase tracking-wide text-sky-900">
                        Documentos firmados · van enteros: un cuestionario no se mezcla campo a campo
                      </div>
                      <ul className="mt-1 ml-4 list-disc text-xs text-sky-900">
                        {d.campos.filter(esDocumento).map(c => (
                          <li key={c.campo}>
                            <strong>{c.etiqueta}:</strong>{' '}
                            {c.accion === 'rellena'
                              ? `esta ficha no tenía ninguno, así que se copia el de la otra entero, tal y como lo firmó el paciente (${c.descartada})`
                              : `queda el de esta ficha (${c.resultado}); el de la otra (${c.descartada}) no se aplica y no se borra: queda entero, con su fecha y su firma, en el archivo de la fusión`}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {d.campos.filter(c => c.accion === 'rellena' && !esAparte(c)).length > 0 && (
                    <div className="mt-2">
                      <div className="text-xs font-semibold uppercase tracking-wide text-emerald-800">Rellena estos huecos</div>
                      <ul className="mt-1 ml-4 list-disc text-xs">
                        {d.campos.filter(c => c.accion === 'rellena' && !esAparte(c)).map(c => (
                          <li key={c.campo}><strong>{c.etiqueta}:</strong> {c.resultado}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* El texto clínico NO se elige: se pega. Si la alergia estaba
                      escrita en la ficha duplicada, tiene que verse en la que
                      queda (supervisión, 2026-09-17).

                      EL RÓTULO DICE SOLO DE LO SUYO (supervisión, 2026-09-18).
                      Antes prometía «no se descarta nada del historial» y tres
                      líneas más abajo el bloque ámbar iba listando lo que sí se
                      descartaba: en la misma pantalla se decían dos cosas
                      contrarias, y la que tranquilizaba era la falsa. Esta
                      promesa vale para ESTOS campos y para ninguno más. */}
                  {d.campos.filter(c => c.accion === 'pega').length > 0 && (
                    <div className="mt-2" data-testid="patients-fusion-pegados">
                      <div className="text-xs font-semibold uppercase tracking-wide text-emerald-800">
                        Texto clínico · se añade DEBAJO de lo que ya había, sin pisar nada
                      </div>
                      <ul className="mt-1 ml-4 list-disc text-xs">
                        {d.campos.filter(c => c.accion === 'pega').map(c => (
                          <li key={c.campo}>
                            <strong>{c.etiqueta}:</strong> se añade «{c.descartada}»
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {d.campos.filter(c => c.accion === 'descarta' && !esAparte(c)).length > 0 && (
                    <div className="mt-2" data-testid="patients-fusion-conflictos">
                      <div className="text-xs font-semibold uppercase tracking-wide text-amber-800">
                        Datos de ficha (no clínicos) que NO se aplican: manda la ficha principal.
                        Estos no se borran: quedan en el archivo de la fusión, que dirección puede
                        abrir desde la ficha del paciente.
                      </div>
                      <ul className="mt-1 ml-4 list-disc text-xs">
                        {d.campos.filter(c => c.accion === 'descarta' && !esAparte(c)).map(c => (
                          <li key={c.campo}>
                            <strong>{c.etiqueta}:</strong> queda «{c.resultado}», se descarta «{c.descartada}»
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {Object.keys(d.referencias || {}).length > 0 ? (
                    <div className="mt-2 text-xs text-muted-foreground" data-testid="patients-fusion-referencias">
                      Se mueven a la ficha que se queda:{' '}
                      {Object.entries(d.referencias).map(([k, v]) => `${v} ${k}`).join(' · ')}
                    </div>
                  ) : (
                    <div className="mt-2 text-xs text-muted-foreground">Esta ficha no tiene nada colgando.</div>
                  )}
                </div>
              ))}

              <AvisoNombreEnLosApuntes />

              <label className="flex items-start gap-2 p-3 rounded-md bg-muted cursor-pointer">
                <input
                  type="checkbox"
                  checked={fusionOk}
                  onChange={(e) => setFusionOk(e.target.checked)}
                  className="mt-0.5"
                  data-testid="patients-fusion-confirm-check"
                />
                <span>He comprobado que son la misma persona y entiendo que esta fusión no se puede deshacer.</span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setFusion(null); setFusionOk(false); }} data-testid="patients-fusion-cancel">
              Cancelar
            </Button>
            <Button
              onClick={aplicarFusion}
              disabled={!fusionOk || fusionBusy}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="patients-fusion-apply"
            >
              {fusionBusy
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Fusionando…</>
                : 'Fusionar definitivamente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ------------------------------------------------------------------
          «FUSIONAR TODOS»: la lista COMPLETA de parejas detectadas, no una
          muestra de tres. Si no se pueden leer todas, no se pueden aprobar
          todas.
          ------------------------------------------------------------------ */}
      <Dialog open={!!todos} onOpenChange={(v) => { if (!v) { setTodos(null); setTodosOk(false); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="patients-dedupe-dialog">
          <DialogHeader>
            <DialogTitle>Fusionar todos los duplicados detectados</DialogTitle>
          </DialogHeader>
          {todos && (
            <div className="space-y-4 text-sm">
              <div className="flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-300 text-red-800">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  <strong>{todos.duplicates_found} parejas.</strong> Se fusionan todas de golpe y no se puede deshacer.
                  Repasa la lista entera antes de decir que sí.
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                Solo se emparejan fichas del mismo teléfono donde una firmó la LOPD (tiene NIF) y la
                otra la creó la agenda (sin NIF). No se emparejan por nombre y teléfono a secas: dos
                hermanas con el mismo móvil no son la misma persona.
              </div>
              <div className="border border-border rounded-md divide-y divide-border" data-testid="patients-dedupe-lista">
                {(todos.pairs || []).map(p => (
                  <div key={`${p.principal_id}-${p.secondary_id}`} className="p-2 text-xs flex flex-wrap items-center gap-2" data-testid="patients-dedupe-pareja">
                    <span className="font-medium text-[#13231E]">
                      {p.principal_name} · nº {p.principal_num_historia ?? '—'} · {p.principal_nif || 'sin NIF'}
                    </span>
                    <ArrowRight className="w-3 h-3 text-muted-foreground" />
                    <span className="text-muted-foreground">
                      absorbe a {p.secondary_name} · nº {p.secondary_num_historia ?? '—'} · {p.secondary_phone || 'sin teléfono'}
                    </span>
                  </div>
                ))}
              </div>
              <AvisoNombreEnLosApuntes />
              <label className="flex items-start gap-2 p-3 rounded-md bg-muted cursor-pointer">
                <input
                  type="checkbox"
                  checked={todosOk}
                  onChange={(e) => setTodosOk(e.target.checked)}
                  className="mt-0.5"
                  data-testid="patients-dedupe-confirm-check"
                />
                <span>He repasado las {todos.duplicates_found} parejas y entiendo que esto no se puede deshacer.</span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setTodos(null); setTodosOk(false); }} data-testid="patients-dedupe-cancel">
              Cancelar
            </Button>
            <Button
              onClick={aplicarDedupe}
              disabled={!todosOk || dedupeBusy}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="patients-dedupe-apply"
            >
              {dedupeBusy
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Fusionando…</>
                : 'Fusionar todos'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PatientFormDialog open={dialogOpen} onOpenChange={setDialogOpen} initial={editing} onSaved={() => fetchItems(q)} />
      <DarCitaDialog
        open={!!darCitaPatient}
        onOpenChange={(v) => { if (!v) setDarCitaPatient(null); }}
        patient={darCitaPatient}
      />
    </div>
  );
}
